import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFolderOpen, FaPlus, FaSpinner, FaTrash, FaCalendarAlt, FaChartPie, FaUsers } from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import LocationPicker from '../components/LocationPicker';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { API_URL } from '../config';

// ============================================
// PROJECT MANAGEMENT — list + create (admin only creates)
// ============================================
const token = () => localStorage.getItem('token');
const apiFetch = (path, opts = {}) => fetch(`${API_URL}${path}`, {
  ...opts,
  headers: {
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
    ...(opts.headers || {})
  }
});

const STATUS_META = {
  planning: { label: 'Planning', color: '#F59E0B', bg: '#FEF3C7' },
  in_progress: { label: 'In Progress', color: '#2563EB', bg: '#DBEAFE' },
  on_hold: { label: 'On Hold', color: '#6B7280', bg: '#F3F4F6' },
  complete: { label: 'Complete', color: '#065F46', bg: '#D1FAE5' }
};
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

function ProjectManagementPage({ onLogout }) {
  const { isLoaded } = useGoogleMaps();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isStaff = ['admin', 'super_admin'].includes(user.role);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const emptyForm = { name: '', client_id: '', description: '', status: 'planning', start_date: '', end_date: '', origin: '', destination: '' };
  const [form, setForm] = useState(emptyForm);
  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const coordOf = (c, axis) => {
    if (!c) return null;
    const v = typeof c[axis] === 'function' ? c[axis]() : c[axis];
    return Number.isFinite(Number(v)) ? Number(v) : null;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/projects');
      const data = await res.json();
      if (data.success) setProjects(data.projects || []);
      else toast.error(data.error || 'Failed to load projects');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (isStaff) {
      fetch(`${API_URL}/clients`, { headers: token() ? { Authorization: `Bearer ${token()}` } : {} })
        .then((r) => r.json())
        .then((d) => { if (d.success) setClients(d.clients || []); })
        .catch(() => {});
    }
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        location: [form.origin, form.destination].filter(Boolean).join(' → ') || null,
        origin_lat: coordOf(originCoords, 'lat'),
        origin_lng: coordOf(originCoords, 'lng'),
        dest_lat: coordOf(destCoords, 'lat'),
        dest_lng: coordOf(destCoords, 'lng')
      };
      const res = await apiFetch('/projects', { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        toast.success(`✅ ${data.project.project_number} created`);
        setShowCreate(false);
        setForm(emptyForm);
        setOriginCoords(null);
        setDestCoords(null);
        load();
      } else toast.error(data.error || 'Failed to create project');
    } catch (err) {
      toast.error('Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      const res = await apiFetch(`/projects/${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { toast.success('Project deleted'); setDeleteTarget(null); load(); }
      else toast.error(data.error || 'Failed to delete');
    } catch { toast.error('Failed to delete'); }
  };

  const shown = projects.filter((p) => !filter || p.status === filter);
  const totals = {
    all: projects.length,
    running: projects.filter((p) => p.status === 'in_progress').length,
    planning: projects.filter((p) => p.status === 'planning').length,
    done: projects.filter((p) => p.status === 'complete').length
  };

  return (
    <AdminLayout activeTab="projects" onLogout={onLogout} user={user}>
      <div className="w-full">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
              <FaFolderOpen className="text-green" /> Project Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">Long-running deliverables — schedule, files, certificates, quotes and loads in one place.</p>
          </div>
          {isStaff && (
            <button
              onClick={() => setShowCreate(true)}
              className="px-4 py-2 bg-green text-white rounded-lg font-medium hover:bg-navy transition-colors flex items-center gap-2"
            >
              <FaPlus size={14} /> New Project
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { k: '', label: 'All projects', value: totals.all, icon: FaChartPie, color: '#0A1929' },
            { k: 'in_progress', label: 'In progress', value: totals.running, icon: FaSpinner, color: '#2563EB' },
            { k: 'planning', label: 'Planning', value: totals.planning, icon: FaCalendarAlt, color: '#F59E0B' },
            { k: 'complete', label: 'Complete', value: totals.done, icon: FaUsers, color: '#00A86B' }
          ].map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.label}
                onClick={() => setFilter(c.k)}
                className={`bg-white rounded-2xl border p-4 text-left transition-shadow hover:shadow-md ${filter === c.k ? 'ring-2 ring-green' : ''}`}
                style={{ borderColor: '#E8ECEF' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wide text-gray-500">{c.label}</span>
                  <Icon style={{ color: c.color }} />
                </div>
                <div className="text-2xl font-bold mt-1" style={{ color: c.color }}>{c.value}</div>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><FaSpinner className="animate-spin text-3xl text-green" /></div>
        ) : shown.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center text-gray-400" style={{ borderColor: '#E8ECEF' }}>
            No projects yet{isStaff ? ' — click “New Project” to start.' : '.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shown.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.planning;
              const pct = p.task_count ? Math.round((p.task_done / p.task_count) * 100) : (p.completion || 0);
              return (
                <div key={p.id} className="bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-400">{p.project_number}</p>
                      <Link to={`/project-management/${p.id}`} className="text-lg font-bold text-navy hover:text-green transition-colors">{p.name}</Link>
                      <p className="text-sm text-gray-500">{p.client?.company_name || p.client?.trading_name || 'No client linked'}</p>
                    </div>
                    <span className="text-[11px] px-2 py-1 rounded-full font-semibold shrink-0" style={{ backgroundColor: meta.bg, color: meta.color }}>{meta.label}</span>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-4">
                    <span className="flex items-center gap-1"><FaCalendarAlt size={11} /> {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</span>
                    <span>{p.task_count || 0} tasks</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Progress</span><span>{pct}%</span></div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <Link to={`/project-management/${p.id}`} className="text-sm text-green font-medium hover:underline">Open project →</Link>
                    {isStaff && (
                      <button onClick={() => setDeleteTarget(p)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                        <FaTrash size={11} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreate(false)}></div>
          <form onSubmit={create} className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-4">New Project</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Transformer transport — Angola" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                  <option value="">— none —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.trading_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                  <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                  <option value="planning">Planning</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Route</label>
                {isLoaded ? (
                  <div className="space-y-3">
                    <LocationPicker
                      label="Origin"
                      value={form.origin}
                      onChange={(v) => setForm((f) => ({ ...f, origin: v }))}
                      onCoordinatesChange={setOriginCoords}
                      placeholder="e.g. Richards Bay"
                    />
                    <LocationPicker
                      label="Destination"
                      value={form.destination}
                      onChange={(v) => setForm((f) => ({ ...f, destination: v }))}
                      onCoordinatesChange={setDestCoords}
                      placeholder="e.g. Lusaka"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="Origin" />
                    <input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="Destination" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy disabled:opacity-50 flex items-center gap-2">
                {saving ? <FaSpinner className="animate-spin" /> : <FaPlus size={12} />} Create Project
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Project"
        message={deleteTarget ? `Delete ${deleteTarget.project_number} — ${deleteTarget.name}? Its tasks, files and links go with it. This cannot be undone.` : ''}
        confirmLabel="Delete"
        danger
        onConfirm={remove}
      />
    </AdminLayout>
  );
}

export default ProjectManagementPage;

